import React from 'react';
import { motion } from 'framer-motion';
import { CardContainer, CardBody, CardItem } from './Card3D';

export default function HeroCarousel({ products = [] }) {
  // Use a limited set of products for the carousel to prevent performance issues
  const { carouselProducts, totalWidth } = React.useMemo(() => {
    if (!products || products.length === 0) return { carouselProducts: [], totalWidth: 0 };
    
    // Take only the first 5 products for the carousel
    const featuredProducts = products.slice(0, 5);
    
    // Duplicate products for infinite scroll effect (enough to fill screen and loop)
    // We need at least 2 sets for perfect loop, 3 provides buffer
    const duplicated = [...featuredProducts, ...featuredProducts, ...featuredProducts];
    
     // 384 = card width (360px) + gap (24px)
    const width = featuredProducts.length * 384;
    
    return { carouselProducts: duplicated, totalWidth: width };
  }, [products]);

  if (carouselProducts.length === 0) return null;

  return (
    <div className="relative w-full h-96 overflow-hidden rounded-xl bg-raya-card shadow-xl border border-raya-border">
      {/* Scrolling Container */}
      <motion.div
        className="flex gap-6 absolute left-0 active:cursor-grabbing cursor-grab"
        animate={{
          x: [-0, -totalWidth]
        }}
        transition={{
          x: {
            repeat: Infinity,
            repeatType: "loop",
            duration: 30, // Adjust speed (seconds to scroll one full set)
            ease: "linear",
          },
        }}
        // Pause on hover
        whileHover={{ animationPlayState: "paused" }} 
      >
        {carouselProducts.map((product, index) => (
          <div
            key={`${product.id}-${index}`}
            className="flex-shrink-0"
            style={{ width: '360px' }}
          >
            <CardContainer 
              className="w-full h-full"
              containerClassName="w-full h-full"
            >
              <CardBody className="relative w-full h-full rounded-lg overflow-hidden bg-raya-bg shadow-2xl border border-raya-border">
                <CardItem
                  translateZ="50"
                  className="w-full h-full"
                >
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-emerald-900 to-navy-900 flex items-center justify-center">
                      <span className="text-8xl">📦</span>
                    </div>
                  )}
                  {/* Gradient Overlay */}
                   <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10" />
                  
                  {/* Product Name Overlay */}
                  <CardItem 
                    translateZ="80"
                    className="absolute bottom-6 left-6 right-6 z-20"
                  >
                    <div className="bg-black/60 backdrop-blur-md rounded-xl p-4 border border-white/10 shadow-lg">
                      <h3 className="text-raya-text-primary font-bold text-lg line-clamp-1 mb-1">
                        {product.name}
                      </h3>
                      <p className="text-raya-accent font-bold text-2xl bg-gradient-to-r from-raya-accent to-raya-accent-hover bg-clip-text text-transparent">
                        ${parseFloat(product.price).toFixed(2)}
                      </p>
                    </div>
                  </CardItem>
                </CardItem>
              </CardBody>
            </CardContainer>
          </div>
        ))}
      </motion.div>

      {/* Left Fade Gradient */}
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-raya-bg to-transparent pointer-events-none z-10" />
      
      {/* Right Fade Gradient */}
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-raya-bg to-transparent pointer-events-none z-10" />
    </div>
  );
}

// Remove the old CSS animations as they're no longer needed
if (document.head.querySelector('style[data-carousel-animations]')) {
  document.head.querySelector('style[data-carousel-animations]').remove();
}
